<template>
  <long-query-layout
    resource-name="river"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :active-tab.sync="activeTab"
    :no-excel-download="true"
    :disable-reservation="true"
    @query-completed="onQueryCompleted"
  >
    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term
          :term-type.sync="queryFilter.term.termType"
          :errors.sync="queryFilter.term.errors"
          :date-from="queryFilter.term.dateFrom"
          :date-to="queryFilter.term.dateTo"
          :default-week-count="8"
          @update:dateFrom="onUpdateDateFrom"
          @update:dateTo="onUpdateDateTo"
        />
      </query-filter-element>

      <!-- 棚割パターン選択 -->
      <query-filter-element id="tanawari" title="棚割パターン指定">
        <gl-select-company :company.sync="queryFilter.tanawari.company" />
        <filter-tanawari-pattern
          v-bind.sync="queryFilter.tanawari"
          :date-from="queryFilter.term.dateFrom"
          :date-to="queryFilter.term.dateTo"
          :exclude-other-category-items.sync="
            queryFilter.tanawari.excludeOtherCategoryItems
          "
          :categories.sync="queryFilter.tanawari.categories"
        />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option" title="オプション">
        <filter-option-river v-bind.sync="queryFilter.option" />
      </query-filter-element>

      <md-snackbar
        md-position="center"
        :md-duration="2000"
        :md-active.sync="snackbar.show"
        md-persistent
      >
        <span>期間が変わったので棚割パターンをクリアしました。</span>
      </md-snackbar>
    </template>
    <template v-slot:querying-spinner>
      <div class="papyrus-container">
        <img
          class="animated shake infinite"
          src="@/assets/images/papyrus.png"
        />
      </div>
    </template>
    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <div>
        <img src="@/assets/images/papyrus.png" />
      </div>
      <div>
        <a :href="downloads.pdf" target="_blank" rel="noopener noreferrer"
          >PDFダウンロード</a
        >
      </div>
      <hr />
      <h4>追加ダウンロード</h4>
      <p>
        BiZOOPeの他の分析メニューから読込可能なテキストファイルなど
      </p>
      <p>
        <a
          :href="downloads.item_codes"
          target="_blank"
          rel="noopener noreferrer"
        >
          商品リスト（CSV）
        </a>
      </p>
      <p>
        <a :href="downloads.stores" target="_blank" rel="noopener noreferrer">
          店舗リスト（CSV）
        </a>
      </p>
      <p>
        <a :href="downloads.excel" target="_blank" rel="noopener noreferrer">
          元ネタExcel
        </a>
      </p>
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTerm from '@/components/FilterTerm.vue'
import FilterTanawariPattern from '@/components/FilterTanawariPattern.vue'
import FilterOptionRiver from '@/components/FilterOptionRiver.vue'
// 分析条件モデル
import term from '@/queryfilter/term'

export default {
  components: {
    QueryFilterElement,
    FilterTerm,
    FilterTanawariPattern,
    FilterOptionRiver,
    LongQueryLayout,
  },
  props: {},
  data() {
    return {
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, ...term.get() },
        tanawari: {
          order: 2,
          // 棚割パターンの選択では分類も選択可能です。
          // 分類マスタのロードにはどうしても企業コードが必要なので
          // 保持しています。
          company: undefined,
          items: [],
          excludeOtherCategoryItems: false,
          categories: [],
          errors: [],
        },
        option: {
          order: 3,
          summaryKey: 'optionRiver',
          scrollToId: 'option',
          y1: 'face',
          y2: 'qty',
          unitPrice: 50,
          visibleFromMinPriceLine: true,
          // 現状APIに顧客属性マスタを取得するエンドポイントがありません。
          // この機能のためだけにAPIを作るのは結構面倒なのでベタ書きします。
          // storeとかに入れてしまうと逆に他の機能も安易に参照してしまう気が
          // するのでこの画面だけのものとします。
          ages: [
            {
              name: '20歳未満',
              value: '01',
            },
            {
              name: '20代',
              value: '02',
            },
            {
              name: '30代',
              value: '03',
            },
            {
              name: '40代',
              value: '04',
            },
            {
              name: '50代',
              value: '05',
            },
            {
              name: '60代',
              value: '06',
            },
            {
              name: '70歳以上',
              value: '07',
            },
            {
              name: 'その他',
              value: '90',
            },
          ],
          ageSummary: ['01', '02', '03', '04', '05', '06', '07', '90', ''],
          ageDisplay: ['01', '02', '03', '04', '05', '06', '07'],
          errors: [],
        },
      },
      downloads: {
        pdf: undefined,
        stores: undefined,
        items: undefined,
        excel: undefined,
      },
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: term.mapToApiPayload(queryFilter.term),
        company: queryFilter.tanawari.company,
        categories: queryFilter.tanawari.excludeOtherCategoryItems
          ? queryFilter.tanawari.categories
          : [],
        patterns: queryFilter.tanawari.items.map(item => ({
          patternCode: item.code,
          patternName: item.name,
          ymd: item.ymd.replace(/-/g, ''),
        })),
        option: {
          y1: queryFilter.option.y1,
          y2: queryFilter.option.y2,
          unitPrice: queryFilter.option.unitPrice,
          visibleFromMinPriceLine: queryFilter.option.visibleFromMinPriceLine,
          ageSummary: Array.from(queryFilter.option.ageSummary),
          ageDisplay: Array.from(queryFilter.option.ageDisplay),
        },
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       *
       * 渡される分析条件はこの画面のものとは限らないので、"可能な限り"復元する。
       */
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        this.queryFilter.tanawari.company = queryFilter.tanawari.company
        this.queryFilter.tanawari.items = queryFilter.tanawari.items
        this.queryFilter.tanawari.excludeOtherCategoryItems =
          queryFilter.tanawari.excludeOtherCategoryItems
        this.queryFilter.tanawari.categories = queryFilter.tanawari.categories
        this.queryFilter.option.y1 = queryFilter.option.y1
        this.queryFilter.option.y2 = queryFilter.option.y2
        this.queryFilter.option.unitPrice = queryFilter.option.unitPrice
        this.queryFilter.option.visibleFromMinPriceLine =
          queryFilter.option.visibleFromMinPriceLine
        this.queryFilter.option.ageSummary = queryFilter.option.ageSummary
        this.queryFilter.option.ageDisplay = queryFilter.option.ageDisplay
      },
      snackbar: {
        show: false,
      },
    }
  },
  methods: {
    onUpdateDate(fromOrTo, v) {
      // 期間の変更を検知して選択パターンをクリアすると
      // 条件復元のときにもクリアされてしまう。
      // とりあえず期間選択コンポーネント起点の変更でしか
      // クリアしないよう頑張る。
      if (fromOrTo && v !== fromOrTo) {
        const hadSomeItems = this.queryFilter.tanawari.items.length > 0
        this.queryFilter.tanawari.items = []
        if (hadSomeItems) {
          this.snackbar.show = true
        }
      }
    },
    onUpdateDateFrom(v) {
      this.onUpdateDate(this.queryFilter.term.dateFrom, v)
      this.queryFilter.term.dateFrom = v
    },
    onUpdateDateTo(v) {
      this.onUpdateDate(this.queryFilter.term.dateTo, v)
      this.queryFilter.term.dateTo = v
    },
    /**
     * クエリ完了
     */
    onQueryCompleted(queryInfo) {
      const data = JSON.parse(queryInfo.data)
      this.downloads.pdf = data.pdf
      this.downloads.excel = data.xlsx
      this.downloads.item_codes = data.item_codes
      this.downloads.stores = data.stores
    },
  },
}
</script>

<style lang="scss" scoped>
.papyrus-container {
  overflow-y: hidden;
  opacity: 0.5;
  animation-name: papyrus_animation;
  animation-duration: 7s;
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
  animation-delay: 0s;
}
@keyframes papyrus_animation {
  0% {
    height: 0%;
  }
  5% {
    height: 10%;
  }
  40% {
    height: 30%;
  }
  60% {
    height: 70%;
  }
  100% {
    height: 100%;
  }
}
</style>
